import { useCallback } from 'react';
import useRequestJSON, {
  IsLoadingSegment,
  RunnerSegment
} from './hooks/useRequestJSON';
import {
  PostJSON,
  BaseResponse,
  PaginatedParams,
  ErrorResponse
} from './request';
import { IUser } from './user';
import { IAccount, IAccountBaseSettings } from './account';

export interface IPMTemplate {
  id: number;
  body: string[];
  app?: string;
  tags?: string[];
  isChatTemplate?: boolean;
  isDeleted?: boolean;
  accountId: number;
  userId: number;
}

export interface IPushMessage {
  id: number;
  eta: string;
  isCanceled: boolean;
  canceledAt: Date;
  messageIds: string[];
  templatId: number;
  userId: number;
  patientId: number;
  bulkId: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface IBulkPushMessage {
  id: number;
  criteria: string;
  task: string;
  templateId: number;
  template?: IPMTemplate;
  userId: number;
  user?: IUser;
  _numMessagesSent?: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface IPMTemplateTag {
  id: number;
  value: string;
  isDeleted?: boolean;
  accountId: number;
}

export interface GetPMTemplatesParams extends PaginatedParams {
  app?: string;
  tag?: string;
  body?: string;
  templateType?: 'chat' | 'pm' | 'all';
  pinnedIds?: number[];
}

export interface GetPMTemplatesResponse extends BaseResponse {
  total: number;
  templates: IPMTemplate[];
  pinnedTemplates?: IPMTemplate[];
}

export interface GetPMTemplatesResult {
  total: number;
  templates: IPMTemplate[];
  pinnedTemplates: IPMTemplate[];
  isLoading: boolean;
}

export interface SendPMResponse extends BaseResponse {
  push: IPushMessage;
}

export interface SendBulkPMResponse extends BaseResponse {
  bulk: IBulkPushMessage;
}

export interface GetPMBulkMessagesResponse extends BaseResponse {
  total: number;
  bulks: IBulkPushMessage[];
}

export interface GetPMBulkMessagesResult
  extends RunnerSegment, IsLoadingSegment {
  total: number;
  bulks: IBulkPushMessage[];
}

export const useGetPMTemplates = (
  params: GetPMTemplatesParams
): GetPMTemplatesResult & RunnerSegment => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/pm/data_templates/',
        params: {
          ...params,
          app: params.app?.trim().length > 0 ? params.app : null,
          body: params.body?.trim().length > 0 ? params.body : null,
          tag: params.tag?.trim().length > 0 ? params.tag : null,
          pinnedIds: params.pinnedIds?.length > 0 ? params.pinnedIds : null
        }
      }),
    [
      params.page,
      params.limit,
      params.app,
      params.tag,
      params.body,
      params.templateType,
      JSON.stringify(params.pinnedIds)
    ]
  );
  const { data, isLoading, run } = useRequestJSON<GetPMTemplatesResponse>({
    request
  });
  return {
    total: data?.total || 0,
    templates: data?.templates || [],
    pinnedTemplates: data?.pinnedTemplates || [],
    isLoading,
    run: () => run(request)
  };
};

export interface GetPMTemplateTagsParams extends PaginatedParams {
  value?: string;
}

export interface GetPMTemplateTagsResponse extends BaseResponse {
  total: number;
  tags: IPMTemplateTag[];
}

export interface GetPMTemplateTagsResult {
  total: number;
  tags: IPMTemplateTag[];
  isLoading: boolean;
}

export const useGetPMTemplateTags = (
  params: GetPMTemplateTagsParams = {}
): GetPMTemplateTagsResult & RunnerSegment => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/pm/data_template_tags/',
        params
      }),
    [params.page, params.limit, params.value]
  );
  const { data, isLoading, run } = useRequestJSON<GetPMTemplateTagsResponse>({
    request
  });
  return {
    total: data?.total || 0,
    tags: data?.tags || [],
    isLoading,
    run: () => run(request)
  };
};

export interface CreatePMTemplateParams {
  app?: string;
  body: string[];
}

export const CreatePMTemplate = (
  params: CreatePMTemplateParams
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/pm/create_template/',
    params: {
      ...params,
      app: params.app?.trim().length > 0 ? params.app : null
    }
  });
};

export const RemovePMTemplate = (
  id: number
): Promise<BaseResponse | ErrorResponse> => {
  return PostJSON<BaseResponse>({
    url: '/pm/delete_template/',
    params: {
      id
    }
  });
};

export const useGetBulkPushes = (
  params: PaginatedParams
): GetPMBulkMessagesResult => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/pm/data_bulk/',
        params
      }),
    [params.page, params.limit]
  );
  const { data, isLoading, run } = useRequestJSON<GetPMBulkMessagesResponse>({
    request
  });
  return {
    total: data?.total || 0,
    bulks: data?.bulks || [],
    isLoading,
    run: () => run(request)
  };
};

export const getCanViewBulkPM = (
  account: IAccount,
  settings: IAccountBaseSettings,
  isAdmin: boolean
): boolean => {
  if (!account?.isPushMessageEnabled) {
    return false;
  }
  let isBulkPMAdminOnly = true;
  if (settings?.pmOptions && settings.pmOptions.isBulkAdminOnly === false) {
    isBulkPMAdminOnly = false;
  }
  let canViewBulkPM = true;
  if (isBulkPMAdminOnly && !isAdmin) {
    canViewBulkPM = false;
  }
  return canViewBulkPM;
};
